'use client';

/** Search field plus the two chip axes (use case, entity) for the modal. */

import React from 'react';
import { Search, X } from 'lucide-react';
import { ALL_KEY, ENTITY_LABELS, USE_CASE_LABELS } from './labels';

function chipClass(isSelected: boolean): string {
  return `rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
    isSelected
      ? 'bg-primary text-primary-foreground'
      : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
  }`;
}

function ChipRow({
  legend,
  keys,
  labels,
  selected,
  onSelect,
}: {
  legend: string;
  keys: readonly string[];
  labels: Record<string, string>;
  selected: string;
  onSelect: (key: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{legend}</span>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onSelect(ALL_KEY)}
          className={chipClass(selected === ALL_KEY)}
        >
          All
        </button>
        {keys.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(key)}
            className={chipClass(selected === key)}
          >
            {labels[key] ?? key}
          </button>
        ))}
      </div>
    </div>
  );
}

export interface CapabilityFilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  useCaseGroups: readonly string[];
  selectedUseCase: string;
  onSelectUseCase: (key: string) => void;
  entities: readonly string[];
  selectedEntity: string;
  onSelectEntity: (key: string) => void;
}

export function CapabilityFilterBar({
  search,
  onSearchChange,
  useCaseGroups,
  selectedUseCase,
  onSelectUseCase,
  entities,
  selectedEntity,
  onSelectEntity,
}: CapabilityFilterBarProps) {
  return (
    <>
      <div className="relative">
        <div className="relative flex items-center">
          <Search className="absolute left-3 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            placeholder="Search capabilities (e.g., company, email, create)..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-3 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <ChipRow
          legend="Use case"
          keys={useCaseGroups}
          labels={USE_CASE_LABELS}
          selected={selectedUseCase}
          onSelect={onSelectUseCase}
        />
        <ChipRow
          legend="Entity"
          keys={entities}
          labels={ENTITY_LABELS}
          selected={selectedEntity}
          onSelect={onSelectEntity}
        />
      </div>
    </>
  );
}
